import axios from 'axios';
import { API_BASE_URL } from '../config';
import * as integrationRoles from '../utils/integrationRoles';

export function erpSendKind(row, objectKey) {
  return integrationRoles.erpSendKind(row, objectKey);
}

export function integrationKind(row) {
  return integrationRoles.integrationKind(row);
}

export function integrationLabel(row) {
  return integrationRoles.integrationLabel(row);
}

export function isErpIntegration(row) {
  return integrationRoles.isErpIntegration(row);
}

export function isProjectToolIntegration(row) {
  return integrationRoles.isProjectToolIntegration(row);
}

export function isSyncableIntegration(row) {
  return integrationRoles.isSyncableIntegration(row);
}

export function sendKindFor(row, objectKey, fallback) {
  return integrationRoles.sendKindFor(row, objectKey, fallback);
}

export function money(value) {
  const amount = Number(value || 0);
  return amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

export function percent(value) {
  const amount = Number(value || 0);
  return `${amount.toFixed(3)}%`;
}

export function periodLabel(period) {
  if (!period) return '—';
  const stamp = period.month && period.year ? `${period.month}/${period.year}` : period.description || period.vuid;
  return `${stamp} ${period.status === 'open' ? '(Open)' : '(Closed)'}`;
}

export function shortDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function statusClass(status) {
  const text = String(status || '').toLowerCase();
  if (['active', 'open', 'approved', 'posted'].includes(text)) return 'open';
  if (['completed', 'complete', 'closed', 'paid'].includes(text)) return 'closed';
  return '';
}

export async function getJson(path, params) {
  const response = await axios.get(`${API_BASE_URL}${path}`, { params });
  return response.data;
}

export async function postJson(path, body) {
  const response = await axios.post(`${API_BASE_URL}${path}`, body);
  return response.data;
}

export async function putJson(path, body) {
  const response = await axios.put(`${API_BASE_URL}${path}`, body);
  return response.data;
}

export async function deleteJson(path) {
  const response = await axios.delete(`${API_BASE_URL}${path}`);
  return response.data;
}

export function integrationError(error) {
  const data = error?.response?.data;
  if (!data) return error?.message || 'Request failed';
  if (typeof data === 'string') return data;
  const parts = [];
  if (data.error) {
    parts.push(typeof data.error === 'string' ? data.error : JSON.stringify(data.error));
  }
  if (data.message && data.message !== data.error) {
    parts.push(data.message);
  }
  if (Array.isArray(data.errors) && data.errors.length) {
    parts.push(
      data.errors
        .slice(0, 8)
        .map((item) => (typeof item === 'string' ? item : JSON.stringify(item)))
        .join('\n')
    );
  }
  return parts.filter(Boolean).join('\n') || 'Request failed';
}

export function asList(data) {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.items)) return data.items;
  if (data && Array.isArray(data.results)) return data.results;
  return [];
}

export function companyName(user) {
  return (
    user?.tenant?.company_name ||
    user?.tenant?.name ||
    user?.company_name ||
    'Claret'
  );
}

export async function loadCostLookups(projectVuid) {
  const [codes, types, projectCodes] = await Promise.all([
    getJson('/api/costcodes').catch(() => []),
    getJson('/api/costtypes').catch(() => []),
    projectVuid ? getJson(`/api/projects/${projectVuid}/cost-codes`).catch(() => []) : Promise.resolve([]),
  ]);
  const codeMap = {};
  asList(codes).concat(asList(projectCodes)).forEach((row) => {
    if (!row?.vuid) return;
    const label = [row.code, row.description].filter(Boolean).join(' — ');
    codeMap[row.vuid] = label || row.vuid;
  });
  const typeMap = {};
  asList(types).forEach((row) => {
    if (!row?.vuid) return;
    typeMap[row.vuid] = row.cost_type || row.abbreviation || row.description || row.vuid;
  });
  return { codeMap, typeMap };
}

export function costCodeLabel(line, codeMap = {}) {
  if (line?.cost_code && typeof line.cost_code === 'object') {
    return [line.cost_code.code, line.cost_code.description].filter(Boolean).join(' — ') || '—';
  }
  if (typeof line?.cost_code === 'string' && line.cost_code) return line.cost_code;
  return codeMap[line?.cost_code_vuid] || '—';
}

export function costTypeLabel(line, typeMap = {}) {
  if (line?.cost_type && typeof line.cost_type === 'object') {
    return line.cost_type.cost_type || line.cost_type.abbreviation || '—';
  }
  if (typeof line?.cost_type === 'string' && line.cost_type) return line.cost_type;
  return typeMap[line?.cost_type_vuid] || '—';
}

export function budgetLineAmount(line) {
  return Number(
    line?.budget_amount ??
    line?.original_budget ??
    line?.original_amount ??
    line?.total_cost ??
    0
  );
}
